import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  onlineUsers: string[];
  sendMessage: (conversationId: string, content: string, type?: string) => void;
  joinConversation: (conversationId: string) => void;
  leaveConversation: (conversationId: string) => void;
  startTyping: (conversationId: string) => void;
  stopTyping: (conversationId: string) => void;
  reactToMessage: (messageId: string, emoji: string) => void;
  markMessageAsRead: (messageId: string, conversationId: string) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated && user) {
      // Initialize Socket.IO connection
      const newSocket = io(import.meta.env.VITE_API_URL || 'https://api.duoweave.com', {
        auth: {
          token: localStorage.getItem('authToken') // You'll need to store this when user logs in
        },
        transports: ['websocket', 'polling'],
        path: '/socket.io'
      });

      // Connection event handlers
      newSocket.on('connect', () => {
        console.log('Connected to server');
        setIsConnected(true);
      });

      newSocket.on('disconnect', () => {
        console.log('Disconnected from server');
        setIsConnected(false);
      });

      newSocket.on('connect_error', (error) => {
        console.error('Connection error:', error);
        setIsConnected(false);
      });

      // Message event handlers
      newSocket.on('new_message', (data) => {
        console.log('New message received:', data);
        // Handle new message - you can emit a custom event or update state
        window.dispatchEvent(new CustomEvent('newMessage', { detail: data }));
      });

      newSocket.on('message_sent', (data) => {
        console.log('Message sent confirmation:', data);
        window.dispatchEvent(new CustomEvent('messageSent', { detail: data }));
      });

      newSocket.on('message_read', (data) => {
        console.log('Message read receipt:', data);
        window.dispatchEvent(new CustomEvent('messageRead', { detail: data }));
      });

      newSocket.on('message_reaction', (data) => {
        console.log('Message reaction:', data);
        window.dispatchEvent(new CustomEvent('messageReaction', { detail: data }));
      });

      newSocket.on('user_typing', (data) => {
        console.log('User typing:', data);
        window.dispatchEvent(new CustomEvent('userTyping', { detail: data }));
      });

      newSocket.on('user_joined', (data) => {
        console.log('User joined conversation:', data);
        window.dispatchEvent(new CustomEvent('userJoined', { detail: data }));
      });

      newSocket.on('user_left', (data) => {
        console.log('User left conversation:', data);
        window.dispatchEvent(new CustomEvent('userLeft', { detail: data }));
      });

      newSocket.on('contact_status_change', (data) => {
        console.log('Contact status changed:', data);
        window.dispatchEvent(new CustomEvent('contactStatusChange', { detail: data }));
      });

      newSocket.on('incoming_voice_call', (data) => {
        console.log('Incoming voice call:', data);
        window.dispatchEvent(new CustomEvent('incomingVoiceCall', { detail: data }));
      });

      newSocket.on('incoming_video_call', (data) => {
        console.log('Incoming video call:', data);
        window.dispatchEvent(new CustomEvent('incomingVideoCall', { detail: data }));
      });

      newSocket.on('voice_call_started', (data) => {
        console.log('Voice call started:', data);
        window.dispatchEvent(new CustomEvent('voiceCallStarted', { detail: data }));
      });

      newSocket.on('voice_call_ended', (data) => {
        console.log('Voice call ended:', data);
        window.dispatchEvent(new CustomEvent('voiceCallEnded', { detail: data }));
      });

      newSocket.on('video_call_started', (data) => {
        console.log('Video call started:', data);
        window.dispatchEvent(new CustomEvent('videoCallStarted', { detail: data }));
      });

      newSocket.on('video_call_ended', (data) => {
        console.log('Video call ended:', data);
        window.dispatchEvent(new CustomEvent('videoCallEnded', { detail: data }));
      });

      newSocket.on('error', (error) => {
        console.error('Socket error:', error);
      });

      setSocket(newSocket);

      // Cleanup on unmount
      return () => {
        newSocket.close();
      };
    } else {
      // Disconnect if not authenticated
      if (socket) {
        socket.close();
        setSocket(null);
        setIsConnected(false);
      }
    }
  }, [isAuthenticated, user]);

  const sendMessage = (conversationId: string, content: string, type: string = 'text') => {
    if (socket && isConnected) {
      socket.emit('send_message', {
        conversationId,
        content,
        type
      });
    }
  };

  const joinConversation = (conversationId: string) => {
    if (socket && isConnected) {
      socket.emit('join_conversation', { conversationId });
    }
  };

  const leaveConversation = (conversationId: string) => {
    if (socket && isConnected) {
      socket.emit('leave_conversation', { conversationId });
    }
  };

  const startTyping = (conversationId: string) => {
    if (socket && isConnected) {
      socket.emit('typing_start', { conversationId });
    }
  };

  const stopTyping = (conversationId: string) => {
    if (socket && isConnected) {
      socket.emit('typing_stop', { conversationId });
    }
  };

  const reactToMessage = (messageId: string, emoji: string) => {
    if (socket && isConnected) {
      socket.emit('react_to_message', { messageId, emoji });
    }
  };

  const markMessageAsRead = (messageId: string, conversationId: string) => {
    if (socket && isConnected) {
      socket.emit('mark_message_read', { messageId, conversationId });
    }
  };

  const value: SocketContextType = {
    socket,
    isConnected,
    onlineUsers,
    sendMessage,
    joinConversation,
    leaveConversation,
    startTyping,
    stopTyping,
    reactToMessage,
    markMessageAsRead
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
